// BankView — star → dollar conversion. Two surfaces in one tab:
//   1. "Your stars" panel — the caller's own balance + a Cash out
//      button. Kid-friendly (big number, big button).
//   2. "Pending approvals" — adults see the household's pending
//      cash-out queue with Approve / Deny actions. Kids see only
//      their own request (read-only).
//
// All members get the same component; adult-only sections gate on
// the current member's `isKid` flag (kids never see the pending
// queue for OTHER members, just their own request status).

import { useEffect, useMemo, useState } from "preact/hooks";
import { api, type BankLedgerEntry, type BankMyPending, type BankPendingRequest, type FamilyStarsMember } from "../../lib/rpc";
import { useMe } from "../../lib/use-me";
import "./bank.css";

interface Props {
  refreshToken: number;
  notify: (msg: string) => void;
  onMutated: () => void;
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function BankView({ refreshToken, notify, onMutated }: Props) {
  const me = useMe();
  const [members, setMembers] = useState<FamilyStarsMember[] | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [myPending, setMyPending] = useState<BankMyPending | null>(null);
  const [pendingAll, setPendingAll] = useState<BankPendingRequest[]>([]);
  const [ledger, setLedger] = useState<BankLedgerEntry[]>([]);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [denyingId, setDenyingId] = useState<number | null>(null);
  const [denyReason, setDenyReason] = useState("");
  const [bumpToken, setBumpToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api.householdsStarsByMember().catch(() => [] as FamilyStarsMember[]),
      api.bankConfig().catch(() => ({ rate: 10, enabled: true })),
      api.bankMyPending().catch(() => null),
      api.bankListPending().catch(() => [] as BankPendingRequest[]),
    ]).then(([m, cfg, mine, all]) => {
      if (cancelled) return;
      setMembers(m);
      setRate(cfg.rate);
      setEnabled(cfg.enabled);
      setMyPending(mine);
      setPendingAll(all);
    });
    return () => { cancelled = true; };
  }, [refreshToken, bumpToken]);

  const myMember = useMemo(
    () => members?.find((m) => m.userId === me?.username) || null,
    [members, me?.username],
  );

  // Load the caller's own ledger so they see recent cash-outs.
  useEffect(() => {
    let cancelled = false;
    if (!me?.username) { setLedger([]); return; }
    void api.bankLedger(me.username, 10)
      .then((entries) => { if (!cancelled) setLedger(entries); })
      .catch(() => { if (!cancelled) setLedger([]); });
    return () => { cancelled = true; };
  }, [refreshToken, bumpToken, me?.username]);

  const isAdult = myMember && !myMember.isKid;
  const refresh = () => { setBumpToken((n) => n + 1); onMutated(); };

  const approve = async (req: BankPendingRequest) => {
    try {
      const r = await api.bankApprove(req.requestId);
      notify(`Approved · ${dollars(r.dollarsCents)} → ${req.requestedByName}`);
      refresh();
    } catch (e: any) { notify(e?.message || "couldn't approve"); }
  };
  const deny = async (req: BankPendingRequest, reason: string | null) => {
    try {
      await api.bankDeny(req.requestId, reason);
      notify(`Denied · ${req.requestedByName} can try again`);
      refresh();
    } catch (e: any) { notify(e?.message || "couldn't deny"); }
  };
  const cancelOwn = async () => {
    if (!myPending) return;
    try {
      // Denying your own request is the cleanest "withdraw" path —
      // adults are the only role allowed to deny, so a kid can't
      // self-cancel from this UI. We still expose the button for
      // adults who change their mind.
      await api.bankDeny(myPending.requestId, "cancelled");
      notify("Request cancelled");
      refresh();
    } catch (e: any) { notify(e?.message || "couldn't cancel"); }
  };

  if (members === null || rate === null) {
    return <div className="bank-loading">Loading…</div>;
  }

  // Bank turned off household-wide. Adults still see the toggle so
  // they can re-enable; kids get a quiet empty state.
  if (!enabled) {
    return (
      <section className="bank-view" data-testid="bank-view">
        {isAdult ? (
          <BankEnabledToggle enabled={enabled} notify={notify} onChanged={refresh} />
        ) : (
          <div className="chores-stub" data-testid="bank-disabled-stub">
            <p>The bank is off right now.</p>
            <p className="chores-stub-sub">Ask a grown-up if you want to turn it back on.</p>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="bank-view" data-testid="bank-view">
      {/* ── Own balance card ────────────────────────────────────── */}
      {myMember && (
        <div className="bank-card bank-own" data-testid="bank-own-card">
          <header className="bank-own-head">
            <span
              className="bank-own-avatar"
              style={{ background: myMember.avatarColor || "var(--accent)" }}
              aria-hidden
            >
              {myMember.avatarEmoji || myMember.displayName.charAt(0).toUpperCase()}
            </span>
            <div className="bank-own-titles">
              <div className="bank-own-name">{myMember.displayName}'s bank</div>
              <div className="bank-own-rate">
                {rate} ⭐ = $1.00
              </div>
            </div>
          </header>
          <div className="bank-own-balances">
            <div className="bank-balance-block">
              <div className="bank-balance-label">
                <span className="bank-piggy" aria-hidden>🐷</span> In the bank
              </div>
              <div className="bank-balance-amount" data-testid="bank-own-dollars">
                {dollars(myMember.dollarsCents)}
              </div>
            </div>
            <div className="bank-balance-block">
              <div className="bank-balance-label">Available to cash out</div>
              <div className="bank-balance-amount" data-testid="bank-own-cashable">
                {dollars(Math.floor((myMember.cashableStars * 100) / rate))}
              </div>
              <div className="bank-balance-sub">
                {myMember.cashableStars}⭐ cashable
              </div>
            </div>
          </div>
          <div className="bank-own-actions">
            {myPending ? (
              <div className="bank-own-pending" data-testid="bank-own-pending">
                <span className="bank-pending-dot" aria-hidden>•</span>
                Pending: {myPending.starsAmount}⭐ → {dollars(myPending.dollarsCents)}
                {isAdult && (
                  <button
                    type="button"
                    className="bank-cancel-link"
                    onClick={cancelOwn}
                  >
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="bank-cashout-btn"
                onClick={() => setRequestModalOpen(true)}
                disabled={myMember.cashableStars < rate}
                data-testid="bank-cashout-open"
                title={myMember.cashableStars < rate
                  ? `Need at least ${rate}⭐ to cash out`
                  : "Convert stars to dollars"}
              >
                💵 Cash out stars
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Adult-only: pending approvals queue ─────────────────── */}
      {isAdult && pendingAll.length > 0 && (
        <div className="bank-card bank-pending-list" data-testid="bank-pending-list">
          <h3 className="bank-card-head">Cash-outs waiting for review</h3>
          <ul className="bank-pending-rows">
            {pendingAll.map((p) => (
              <li key={p.requestId} className="bank-pending-row">
                <div className="bank-pending-main">
                  <strong>{p.requestedByName}</strong>
                  <span className="bank-pending-detail">
                    {p.starsAmount}⭐ → {dollars(p.dollarsCents)}
                  </span>
                </div>
                {denyingId === p.requestId ? (
                  <div className="bank-deny-form">
                    <input
                      type="text"
                      className="bank-deny-reason"
                      placeholder="Why? (optional)"
                      value={denyReason}
                      maxLength={200}
                      onInput={(e) => setDenyReason((e.target as HTMLInputElement).value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          deny(p, denyReason.trim() || null);
                          setDenyingId(null);
                          setDenyReason("");
                        } else if (e.key === "Escape") {
                          setDenyingId(null);
                          setDenyReason("");
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="bank-deny-confirm"
                      onClick={() => { deny(p, denyReason.trim() || null); setDenyingId(null); setDenyReason(""); }}
                    >Deny</button>
                    <button
                      type="button"
                      className="bank-deny-cancel"
                      onClick={() => { setDenyingId(null); setDenyReason(""); }}
                    >Cancel</button>
                  </div>
                ) : (
                  <div className="bank-pending-actions">
                    <button
                      type="button"
                      className="bank-approve"
                      onClick={() => approve(p)}
                      data-testid={`bank-approve-${p.requestId}`}
                    >✓ Approve</button>
                    <button
                      type="button"
                      className="bank-deny"
                      onClick={() => { setDenyingId(p.requestId); setDenyReason(""); }}
                      data-testid={`bank-deny-${p.requestId}`}
                    >Deny</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Recent ledger entries ────────────────────────────────── */}
      {ledger.length > 0 && (
        <div className="bank-card bank-ledger">
          <h3 className="bank-card-head">Recent activity</h3>
          <ul className="bank-ledger-rows">
            {ledger.map((e) => (
              <li key={e.id} className="bank-ledger-row" data-positive={e.deltaCents > 0}>
                <span className="bank-ledger-reason">{e.reason || "Adjustment"}</span>
                <span className="bank-ledger-amount">
                  {e.deltaCents > 0 ? "+" : ""}{dollars(e.deltaCents)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Admin-only settings ──────────────────────────────────── */}
      {isAdult && (
        <>
          <RateSetting initialRate={rate} notify={notify} onChanged={refresh} />
          <BankEnabledToggle enabled={enabled} notify={notify} onChanged={refresh} />
        </>
      )}

      {requestModalOpen && myMember && (
        <CashOutRequestModal
          member={myMember}
          rate={rate}
          notify={notify}
          onClose={() => setRequestModalOpen(false)}
          onSubmitted={() => { setRequestModalOpen(false); refresh(); }}
        />
      )}
    </section>
  );
}

// ── Cash-out request modal ──────────────────────────────────────

interface CashOutRequestModalProps {
  member: FamilyStarsMember;
  rate: number;
  notify: (msg: string) => void;
  onClose: () => void;
  onSubmitted: () => void;
}

export function CashOutRequestModal({ member, rate, notify, onClose, onSubmitted }: CashOutRequestModalProps) {
  const maxStars = member.cashableStars;
  // Default to the biggest round-dollar amount that fits.
  const startMaxDollars = Math.floor(maxStars / rate);
  const [stars, setStars] = useState(startMaxDollars > 0 ? startMaxDollars * rate : Math.min(rate, maxStars));
  const [submitting, setSubmitting] = useState(false);
  const cents = Math.floor((stars * 100) / rate);

  const submit = async () => {
    if (stars < 1 || stars > maxStars) return;
    setSubmitting(true);
    try {
      await api.bankRequest(stars);
      notify(`Sent ${stars}⭐ for review`);
      onSubmitted();
    } catch (e: any) { notify(e?.message || "couldn't submit"); setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet bank-request-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Cash out request">
        <header className="bank-request-head">
          <div className="bank-request-title">Cash out stars</div>
          <button type="button" className="bank-request-close" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="bank-request-body">
          <p className="bank-request-explain">
            {member.isKid
              ? "An adult will check the request before stars become dollars."
              : "You can self-approve from the pending list after submitting."}
          </p>
          <div className="bank-request-amounts">
            <div>
              <div className="bank-request-amount-label">Stars to convert</div>
              <input
                className="bank-request-amount"
                type="number"
                min={1}
                max={maxStars}
                step={1}
                value={stars}
                onInput={(e) => {
                  const v = Math.max(1, Math.min(maxStars, Number((e.currentTarget as HTMLInputElement).value) || 1));
                  setStars(v);
                }}
              />
              <div className="bank-request-amount-sub">{maxStars} available</div>
            </div>
            <div className="bank-request-equals" aria-hidden>→</div>
            <div>
              <div className="bank-request-amount-label">You'll get</div>
              <div className="bank-request-result" data-testid="bank-request-result">
                ${(cents / 100).toFixed(2)}
              </div>
              <div className="bank-request-amount-sub">at {rate} ⭐ = $1</div>
            </div>
          </div>
        </div>
        <footer className="bank-request-foot">
          <button type="button" className="bank-request-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="bank-request-submit"
            onClick={submit}
            disabled={submitting || stars < 1 || stars > maxStars}
            data-testid="bank-request-submit"
          >
            {submitting ? "Sending…" : "Send for review"}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── Admin rate setting ──────────────────────────────────────────

function BankEnabledToggle({ enabled, notify, onChanged }: { enabled: boolean; notify: (s: string) => void; onChanged: () => void }) {
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.bankSetEnabled(!enabled);
      notify(enabled ? "Bank turned off" : "Bank turned on");
      onChanged();
    } catch (e: any) { notify(e?.message || "couldn't update"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bank-card bank-rate-card" data-testid="bank-enabled-card">
      <h3 className="bank-card-head">Bank visibility</h3>
      <p className="bank-rate-explain">
        Turn off the bank household-wide. Existing balances stay saved
        for when you turn it back on.
      </p>
      <div className="bank-rate-row">
        <span className="bank-rate-label">
          Bank is currently <strong>{enabled ? "on" : "off"}</strong>.
        </span>
        <button
          type="button"
          className="bank-rate-save"
          onClick={toggle}
          disabled={saving}
          data-testid="bank-enabled-toggle"
        >
          {saving ? "Saving…" : enabled ? "Turn off" : "Turn on"}
        </button>
      </div>
    </div>
  );
}

function RateSetting({ initialRate, notify, onChanged }: { initialRate: number; notify: (s: string) => void; onChanged: () => void }) {
  const [rate, setRate] = useState(initialRate);
  const [saving, setSaving] = useState(false);
  const dirty = rate !== initialRate;
  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api.bankSetRate(rate);
      notify(`Rate set to ${rate}⭐ = $1`);
      onChanged();
    } catch (e: any) { notify(e?.message || "couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bank-card bank-rate-card" data-testid="bank-rate-card">
      <h3 className="bank-card-head">Conversion rate</h3>
      <p className="bank-rate-explain">How many stars equal $1 across the household.</p>
      <div className="bank-rate-row">
        <label className="bank-rate-label">
          <input
            className="bank-rate-input"
            type="number"
            min={1}
            max={1000}
            step={1}
            value={rate}
            onInput={(e) => setRate(Math.max(1, Math.min(1000, Number((e.currentTarget as HTMLInputElement).value) || 1)))}
            data-testid="bank-rate-input"
          />
          <span>⭐ = $1.00</span>
        </label>
        <button
          type="button"
          className="bank-rate-save"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="bank-rate-save"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
